/**
 * Panel-level styles only — component-level styles live in src/ui-kit.
 */
import { createStyles, makeStyles, Theme } from "@material-ui/core";
import { UIKIT_COLORS } from "../../ui-kit/theme/colors";

export const useStyles = makeStyles((_theme: Theme) =>
  createStyles({

    // ── Root ──────────────────────────────────────────────────────────────
    panelRoot: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      maxHeight: "100vh",
      width: "100%",
      backgroundColor: UIKIT_COLORS.WHITE,
      fontFamily: "'Montserrat', sans-serif",
      overflow: "hidden",
      boxSizing: "border-box",
    },

    // ── Header ───────────────────────────────────────────────────────────
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "20px 24px 21px",
      borderBottom: `1px solid ${UIKIT_COLORS.BORDER}`,
      backgroundColor: UIKIT_COLORS.WHITE,
      flexShrink: 0,
      zIndex: 4,
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      "& > * + *": { marginLeft: 12 },
    },
    headerTitle: {
      fontSize: 20,
      fontWeight: 700,
      color: UIKIT_COLORS.TEXT_PRIMARY,
      lineHeight: "28px",
      margin: 0,
    },
    statusBadge: {
      backgroundColor: `${UIKIT_COLORS.WARNING_BG} !important`,
      border: `1px solid ${UIKIT_COLORS.WARNING_BORDER}`,
      borderRadius: 6,
      fontSize: 12,
      fontWeight: 600,
      color: `${UIKIT_COLORS.WARNING_TEXT} !important`,
      lineHeight: "16px",
      whiteSpace: "nowrap",
      "& .MuiChip-label": { padding: "5px 13px" },
    },
    closeButton: {
      cursor: "pointer",
      padding: "6px 8px",
      borderRadius: "50%",
      border: "none",
      background: "transparent",
      flexShrink: 0,
      "& .MuiSvgIcon-root": { fontSize: 14 },
      "&:hover": { backgroundColor: UIKIT_COLORS.ICON_BTN_HOVER_BG },
    },

    // ── Scroll Area ──────────────────────────────────────────────────────
    scrollArea: {
      flex: "1 1 0",
      minHeight: 0,           // CRITICAL: enables flex child to scroll
      overflowY: "auto",
      overflowX: "hidden",
      backgroundColor: UIKIT_COLORS.BG_LIGHT,
      padding: 24,
      boxSizing: "border-box",
      "& > * + *": { marginTop: 24 },
    },

    // ── Layout helper used inside sections ───────────────────────────────
    fieldsRow: {
      display: "flex",
      width: "100%",
      alignItems: "flex-start",
      "& > * + *": { marginLeft: 24 },
    },

    // ── Footer ───────────────────────────────────────────────────────────
    footer: {
      display: "flex",
      padding: "25px 24px 24px",
      borderTop: `1px solid ${UIKIT_COLORS.BORDER_FOOTER}`,
      backgroundColor: UIKIT_COLORS.WHITE,
      flexShrink: 0,
      zIndex: 2,
      "& > * + *": { marginLeft: 16 },
    },
    footerRejectBtn: {
      flex: "1 0 0",
      backgroundColor: `${UIKIT_COLORS.DANGER} !important`,
      border: "none",
      borderRadius: 12,
      padding: "8px 24px !important",
      minHeight: "unset",
      fontSize: 14,
      fontWeight: 700,
      color: `${UIKIT_COLORS.WHITE} !important`,
      lineHeight: "20px",
      textTransform: "none",
      "& .MuiButton-label": { fontSize: 14, fontWeight: 700 },
      "&:hover": { backgroundColor: `${UIKIT_COLORS.DANGER_HOVER} !important` },
      "&.Mui-disabled": { backgroundColor: `${UIKIT_COLORS.DANGER} !important`, opacity: 0.4 },
    },
    
    footerApproveBtn: {
      flex: "1 0 0",
      backgroundColor: `${UIKIT_COLORS.PRIMARY} !important`,
      border: "none",
      borderRadius: 12,
      padding: "8px 24px !important",
      minHeight: "unset",
      fontSize: 14,
      fontWeight: 700,
      color: `${UIKIT_COLORS.WHITE} !important`,
      lineHeight: "20px",
      textTransform: "none",
      "& .MuiButton-label": { fontSize: 14, fontWeight: 700 },
      "&:hover": { backgroundColor: `${UIKIT_COLORS.PRIMARY_HOVER} !important` },
      "&.Mui-disabled": { backgroundColor: `${UIKIT_COLORS.PRIMARY} !important`, opacity: 0.4 },
    },

    // ── Section reject controls ───────────────────────────────────────────
    sectionRejectRow: {
      display: "flex",
      justifyContent: "flex-end",
      marginTop: 12,
    },
    rejectSectionBtn: {
      fontSize: 12,
      fontWeight: 600,
      color: `${UIKIT_COLORS.DANGER} !important`,
      border: `1px solid ${UIKIT_COLORS.DANGER} !important`,
      borderRadius: 8,
      padding: "4px 12px",
      textTransform: "none" as const,
      minWidth: "unset",
      lineHeight: "20px",
      "&:hover": { backgroundColor: `${UIKIT_COLORS.DANGER_BG} !important` },
    },
    rejectedBanner: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      backgroundColor: UIKIT_COLORS.DANGER_BG,
      borderLeft: `3px solid ${UIKIT_COLORS.DANGER}`,
      borderRight: `3px solid ${UIKIT_COLORS.DANGER}`,
      borderRadius: 6,
      padding: "6px 10px",
      boxSizing: "border-box" as const,
    },
    rejectedBannerText: {
      fontSize: 12,
      fontWeight: 600,
      color: UIKIT_COLORS.DANGER,
      whiteSpace: "nowrap" as const,
      flexShrink: 0,
    },
    undoRejectBtn: {
      fontSize: 11,
      fontWeight: 500,
      color: `${UIKIT_COLORS.TEXT_SECONDARY} !important`,
      textDecoration: "underline",
      textTransform: "none" as const,
      padding: "0 2px",
      minWidth: "unset",
      flexShrink: 0,
      marginLeft: "auto",
      "&:hover": { background: "transparent", color: `${UIKIT_COLORS.DANGER} !important` },
    },
    inlinePicker: {
      marginBottom: 12,
    },

    // ── Field selection (edit mode) ────────────────────────────────────────
    selectHintRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 4,
    },
    selectHintText: {
      fontSize: 13,
      fontWeight: 500,
      color: UIKIT_COLORS.TEXT_SECONDARY,
      lineHeight: "18px",
    },
    selectHintCount: {
      fontSize: 12,
      fontWeight: 500,
      color: UIKIT_COLORS.TEXT_MUTED,
      lineHeight: "16px",
      flexShrink: 0,
    },
    selectHintDivider: {
      fontSize: 12,
      color: UIKIT_COLORS.TEXT_MUTED,
      flexShrink: 0,
    },
    selectHintActions: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexShrink: 0,
    },
    selectAllBtn: {
      fontSize: 12,
      fontWeight: 600,
      color: `${UIKIT_COLORS.PRIMARY} !important`,
      textDecoration: "underline",
      textTransform: "none" as const,
      padding: "0 2px",
      minWidth: "unset",
      "&:hover": { background: "transparent" },
    },
    selectableField: {
      display: "flex",
      alignItems: "flex-start",
      flex: "1 1 0",
      minWidth: 0,
      width: "100%",
      gap: 8,
      padding: 8,
      borderRadius: 8,
      border: `1px dashed ${UIKIT_COLORS.BORDER_DARK}`,
      boxSizing: "border-box" as const,
      transition: "border-color 120ms ease, background-color 120ms ease",
    },
    selectableFieldSelected: {
      border: `1px solid ${UIKIT_COLORS.DANGER}`,
      backgroundColor: UIKIT_COLORS.DANGER_BG,
    },
    selectableFieldContent: {
      flex: "1 1 0",
      minWidth: 0,
    },
    selectCheckbox: {
      padding: "0 !important",
      marginTop: 2,
      flexShrink: 0,
      color: `${UIKIT_COLORS.TEXT_MUTED} !important`,
      // The app-wide MuiCheckbox theme override renders the tick via a fixed-color
      // background-image (see config/theme/baseTheme.ts), not `color`/currentColor,
      // so recoloring the checked state means swapping that image, not just `color`.
      "&.Mui-checked": {
        color: `${UIKIT_COLORS.DANGER} !important`,
        backgroundImage:
          "url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3QgeD0iMS41IiB5PSIxLjUiIHdpZHRoPSIyMSIgaGVpZ2h0PSIyMSIgcng9IjIuNSIgZmlsbD0iI0REMjMyNiIgZmlsbC1vcGFjaXR5PSIwLjEiIHN0cm9rZT0iI0REMjMyNiIvPgo8cGF0aCBkPSJNNiAxMS44NTVMMTAuMDk2IDE2TDE4IDcuNzg3TDE3LjI0MyA3TDEwLjA4NSAxNC40MzdMNi43NDcgMTEuMDU3TDYgMTEuODU1WiIgZmlsbD0iI0REMjMyNiIvPgo8L3N2Zz4K) !important",
      },
    },
    
  })
);
